import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { AutomationScope } from "@/lib/data/schemas/automations";
import { automationUploadersQueryOptions } from "@/lib/data/react-query/automations";
import { automationError } from "./automationError";
export function AutomationScopeControls({
  scope,
  availableScopes,
  locked,
  onScopeChange,
  limited,
  onLimitedChange,
  uploaderIds,
  onUploadersChange,
}: {
  scope: AutomationScope;
  availableScopes: AutomationScope[];
  locked: boolean;
  onScopeChange: (value: AutomationScope) => void;
  limited: boolean;
  onLimitedChange: (value: boolean) => void;
  uploaderIds: string[];
  onUploadersChange: (ids: string[]) => void;
}) {
  const [search, setSearch] = useState("");
  const people = useInfiniteQuery({
    ...automationUploadersQueryOptions(search),
    enabled: scope === "firm" && limited,
  });
  return (
    <fieldset className="space-y-4">
      <legend className="mb-3 font-medium">Automation type</legend>
      <div className="grid grid-cols-2 gap-3">
        {availableScopes.map((value) => (
          <label
            key={value}
            className={locked ? "cursor-default" : "cursor-pointer"}
          >
            <input
              type="radio"
              name="automationScope"
              className="peer sr-only"
              value={value}
              checked={scope === value}
              disabled={locked}
              onChange={() => {
                onScopeChange(value);
              }}
            />
            <span className="border-input peer-checked:border-primary peer-checked:bg-primary/5 peer-focus-visible:ring-ring flex items-center justify-between gap-3 rounded-lg border px-4 py-4 text-sm font-medium peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2">
              {value === "firm" ? "Firm" : "Personal"}
              <span
                aria-hidden="true"
                className={`flex h-5 w-5 items-center justify-center rounded border ${scope === value ? "border-primary bg-primary text-primary-foreground" : "border-input bg-background"}`}
              >
                {scope === value && <Check className="h-3.5 w-3.5" />}
              </span>
            </span>
          </label>
        ))}
      </div>
      {scope === "firm" && (
        <>
          <label className="flex flex-col items-start gap-3 text-sm">
            <span>When files are uploaded by</span>
            <select
              aria-label="Uploaders"
              className="bg-background rounded-md border px-4 py-3"
              value={limited ? "selected" : "anyone"}
              onChange={(event) => {
                onLimitedChange(event.target.value === "selected");
              }}
            >
              <option value="anyone">Anyone in the firm</option>
              <option value="selected">Selected people</option>
            </select>
          </label>
          {limited && (
            <div className="space-y-4 rounded-lg border p-4">
              <Input
                aria-label="Find uploaders"
                placeholder="Find a person…"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                }}
                maxLength={200}
              />
              {people.error && (
                <p role="alert">{automationError(people.error)}</p>
              )}
              <p className="text-muted-foreground text-xs">
                {uploaderIds.length} people selected
              </p>
              <div className="space-y-3">
                {people.data?.pages
                  .flatMap((page) => page.items)
                  .map((person) => (
                    <label
                      key={person.id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={uploaderIds.includes(person.id)}
                        disabled={
                          !uploaderIds.includes(person.id) &&
                          uploaderIds.length >= 200
                        }
                        onChange={(event) => {
                          onUploadersChange(
                            event.target.checked
                              ? [...uploaderIds, person.id]
                              : uploaderIds.filter((id) => id !== person.id),
                          );
                        }}
                      />
                      {person.name}
                    </label>
                  ))}
              </div>
              {people.isPending && <p role="status">Loading people…</p>}
              {people.hasNextPage && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={people.isFetchingNextPage}
                  onClick={() => void people.fetchNextPage()}
                >
                  More people
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </fieldset>
  );
}
